import { fetchRequest, reactQueryGet } from "../helpers/backendRequestHelpers";
import { internalApi } from "../helpers/urlHelpers";
import type { Post } from "../types";

interface IndexParams {
  params: Record<string, unknown>;
  spaceId: number;
}

const index = ({ spaceId, params }: IndexParams) =>
  reactQueryGet(
    internalApi.posts.index({
      spaceId,
      params,
    }),
  );

interface ShowParams {
  postSlug: string;
  spaceId: number;
}

const show = <T>({ spaceId, postSlug }: ShowParams) =>
  fetchRequest<T>(internalApi.posts.show({ spaceId, postSlug }), {
    method: "GET",
  });

interface CreateParams {
  formData: Partial<PostFormData>;
  post: Partial<Post>;
}

const create = <T>({ post, formData }: CreateParams) =>
  fetchRequest<T>(internalApi.posts.create({ spaceId: post.space_id }), {
    method: "POST",
    body: JSON.stringify({ post: formData }),
  });

interface UpdateParams {
  formData: Partial<PostFormData>;
  post: Partial<Post>;
}

const update = <T>({ post, formData }: UpdateParams) =>
  fetchRequest<T>(
    internalApi.posts.update({ spaceId: post.space_id, postSlug: post.slug }),
    {
      method: "PATCH",
      body: JSON.stringify({ post: formData }),
    },
  );

interface PostParams {
  post: Post;
}

const destroy = <T>({ post }: PostParams) =>
  fetchRequest<T>(
    internalApi.posts.update({ spaceId: post.space_id, postSlug: post.slug }),
    {
      method: "DELETE",
      forReactQuery: true,
    },
  );

const hideFromFeaturedAreas = <T>({ post }: PostParams) =>
  fetchRequest<T>(
    internalApi.posts.featuredAreasVisibility.destroy({
      postSlug: post.slug,
      spaceId: post.space_id,
    }),
    {
      method: "DELETE",
      forReactQuery: true,
    },
  );

const showInFeaturedAreas = <T>({ post }: PostParams) =>
  fetchRequest<T>(
    internalApi.posts.featuredAreasVisibility.create({
      postSlug: post.slug,
      spaceId: post.space_id,
    }),
    {
      method: "POST",
      forReactQuery: true,
    },
  );

const updateKnowledgeBasePost = <T>({ post }: PostParams) =>
  fetchRequest<T>(
    internalApi.posts.updateKnowledgeBasePost({
      postId: post.id,
    }),
    {
      method: "PATCH",
    },
  );

interface PinParams {
  formData: {
    is_pinned: boolean;
    pin_to_top: boolean;
  };
  post?: Post;
}

const pin = ({ post, formData }: PinParams) =>
  fetchRequest<Post>(
    internalApi.posts.pin({ spaceId: post?.space_id, postSlug: post?.slug }),
    {
      method: "PATCH",
      body: JSON.stringify({ post: formData }),
      forReactQuery: true,
    },
  );

export interface PostFormData {
  id?: number;
  name: string;
  space_id?: number;
  tiptap_body: any;
}

interface CreateOrUpdateParams {
  formData: Partial<PostFormData>;
  post: Partial<Post>;
}

const createOrUpdate = ({ post, formData }: CreateOrUpdateParams) =>
  post.id
    ? update<Response>({ post, formData })
    : create<Response>({ post, formData });

export const postApi = {
  index,
  createOrUpdate,
  show,
  destroy,
  pin,
  hideFromFeaturedAreas,
  showInFeaturedAreas,
  updateKnowledgeBasePost,
};
